/**
 * Structured error model for the Koha REST API.
 *
 * Koha returns errors in the shape:
 * ```json
 * { "error": "Human-readable message", "error_code": "machine_code" }
 * ```
 *
 * This model wraps that response and classifies it so callers
 * can distinguish auth failures from permission issues, validation
 * errors, and server-side problems without inspecting raw status codes.
 */
export type KohaErrorType =
  | 'AUTH'        // 401 — credentials invalid or session expired
  | 'PERMISSION'  // 403 — authenticated but lacks required Koha permission
  | 'VALIDATION'  // 400 / 422 — malformed request or invalid field values
  | 'NOT_FOUND'   // 404 — resource does not exist
  | 'SERVER'      // 5xx — Koha-side failure
  | 'NETWORK';    // 0 / timeout — transport-layer failure

export interface KohaApiError {
  /** Classification of the error for switch/case handling */
  type: KohaErrorType;

  /** HTTP status code (0 for network failures) */
  status: number;

  /** Human-readable error message from Koha (or a fallback) */
  error: string;

  /** Machine-readable error code from Koha (e.g. 'invalid_due_date') */
  errorCode: string;

  /** Whether this error type is safe to retry automatically */
  retryable: boolean;
}

/**
 * Parse an HttpErrorResponse into a structured KohaApiError.
 *
 * @param status - HTTP status code
 * @param body - Response body (may be string, object, or null)
 * @returns Classified KohaApiError
 */
export function parseKohaError(status: number, body: any): KohaApiError {
  const kohaError = typeof body === 'object' && body !== null ? body : {};
  let errorMessage: string = kohaError.error || kohaError.message;
  
  if (!errorMessage && Array.isArray(kohaError.errors)) {
    errorMessage = kohaError.errors.map((e: any) => `${e.path || 'Field'}: ${e.message}`).join(' | ');
  }
  
  errorMessage = errorMessage || fallbackMessage(status);
  const errorCode: string = kohaError.error_code || '';

  return {
    type: classifyStatus(status),
    status,
    error: errorMessage,
    errorCode,
    retryable: isRetryable(status),
  };
}

function classifyStatus(status: number): KohaErrorType {
  if (status === 0) return 'NETWORK';
  if (status === 401) return 'AUTH';
  if (status === 403) return 'PERMISSION';
  if (status === 404) return 'NOT_FOUND';
  if (status === 400 || status === 422) return 'VALIDATION';
  if (status >= 500) return 'SERVER';
  return 'NETWORK'; // fallback for unexpected codes
}

function isRetryable(status: number): boolean {
  // 401 is retryable exactly once (after credential refresh)
  // 5xx and network errors can be retried with backoff
  return status === 0 || status === 401 || status >= 500;
}

function fallbackMessage(status: number): string {
  switch (status) {
    case 0:   return 'Network error — could not reach the server';
    case 401: return 'Authentication failed — check credentials';
    case 403: return 'Insufficient permissions for this operation';
    case 404: return 'Resource not found';
    case 422: return 'Validation error — check request data';
    default:  return status >= 500
      ? 'Koha server error — please try again later'
      : `Unexpected error (HTTP ${status})`;
  }
}
